/**
 * Tenant LLM provider config — proxy-only.
 *
 * Provider / model / API key live in Origin's tenant configuration,
 * encrypted server-side. Reads come from
 * `GET /v1/tenant/llm-provider-config` (gated by tenant.llm_config.read, so
 * analyst roles that use the chat can read the descriptor without the admin
 * tenant.tenant_config.read surface). The read falls back to the legacy
 * `GET /v1/tenant/configuration` on 403/404/501 so chat survives a backend
 * that predates this endpoint and custom roles not yet granted the new
 * permission (see fetchLlmProviderConfig). Writes go to
 * `PUT /v1/tenant/configuration` (admin-only). The API key is never
 * returned to the browser — only `has_api_key`. Chat completions stream
 * through the backend LLM proxy with the Origin JWT in the auth header
 * so the provider key never leaves the server.
 *
 * Returns:
 *   { tenantId, role, isAdmin, provider, model, taskModel, hasApiKey,
 *     baseUrl, customHostAllowlist, customAuthStyle, managedDefault }
 *
 * `baseUrl` / `customHostAllowlist` are only meaningful for the `custom`
 * (BYO-gateway) provider — empty strings otherwise. The allowlist is a
 * newline-separated host list the backend proxy enforces (SSRF guard).
 *
 * `managedDefault` is true when this tenant has no bring-your-own key
 * but is being served chat on Origin's managed key. In that case `provider` and
 * `model` carry the managed defaults and `hasApiKey` is false, yet chat is
 * usable — treat `hasApiKey || managedDefault` as "chat is configured".
 *
 * `taskModel` is a per-browser preference (which cheap model sub-agents
 * / judges use) — not a secret, stored in localStorage. The primary
 * provider+key pair is tenant-shared and lives on the backend only.
 */

import { notifyChatSettingsChanged } from "./chatSettingsChanges.js";
import { authedFetch } from "./restClient.js";
import { getCurrentUser } from "./userInfo.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";

// Write path (admin-only): the full tenant-configuration PUT.
const TENANT_CONFIG_PATH = "/v1/tenant/configuration";
// Read path: the narrow LLM-provider-descriptor GET, gated by
// tenant.llm_config.read so the chat works for analyst roles.
const LLM_PROVIDER_CONFIG_PATH = "/v1/tenant/llm-provider-config";
const TASK_MODEL_PREFIX = "origin.chat.taskModel.v2";
const LEGACY_TASK_MODEL_PREFIX = "tenant";

function providerEnumToId(value) {
  switch (value) {
    case "LLM_PROVIDER_ANTHROPIC": case 1: return "anthropic";
    case "LLM_PROVIDER_OPENROUTER": case 2: return "openrouter";
    case "LLM_PROVIDER_OPENAI":     case 3: return "openai";
    case "LLM_PROVIDER_CUSTOM":     case 4: return "custom";
    default: return "";
  }
}

function providerIdToEnum(id) {
  switch (id) {
    case "anthropic":  return 1;
    case "openrouter": return 2;
    case "openai":     return 3;
    case "custom":     return 4;
    default: return 0;
  }
}

// Custom (BYO-gateway) auth scheme. The gateway authenticates with either
// `Authorization: Bearer` (OpenAI-style) or `api-key:` (Azure OpenAI). Only
// meaningful when provider === "custom"; the backend defaults absent/
// unspecified to bearer.
function authStyleEnumToId(value) {
  switch (value) {
    case "LLM_CUSTOM_AUTH_STYLE_BEARER":  case 1: return "bearer";
    case "LLM_CUSTOM_AUTH_STYLE_API_KEY": case 2: return "api-key";
    default: return ""; // UNSPECIFIED / absent → caller defaults to bearer
  }
}

function authStyleIdToEnum(id) {
  switch (id) {
    case "bearer":  return 1;
    case "api-key": return 2;
    default: return 0;
  }
}

// ── Task model (per-browser pref, not a secret) ──────────────────

function taskModelKey(tenantId) {
  return browserStorageKey(TASK_MODEL_PREFIX, tenantId);
}

function readTaskModel(tenantId) {
  if (!tenantId) return "";
  try {
    discardLegacyTenantEntry(localStorage, LEGACY_TASK_MODEL_PREFIX, `${tenantId}:externalLlmTaskModel`);
    const key = taskModelKey(tenantId);
    return key ? localStorage.getItem(key) || "" : "";
  } catch { return ""; }
}

function writeTaskModel(tenantId, value) {
  if (!tenantId) return;
  try {
    const key = taskModelKey(tenantId);
    if (!key) return;
    if (value == null || value === "") localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {}
}

// ── Backend helpers ──────────────────────────────────────────────

// Statuses on the narrow endpoint that mean "retry the legacy admin path":
//   404 / 501 — backend predates GetLlmProviderConfig. A dashboard-before-
//               backend deploy, or a backend rollback after the dashboard
//               shipped, would otherwise hard-break chat creds.
//   403       — a *custom* tenant role that reads chat creds today via the
//               broad tenant.tenant_config.read but was never granted the
//               new tenant.llm_config.read. Builtin roles are backfilled;
//               custom ones are not, so without this they'd 403 post-deploy.
// Falling back never widens access: /v1/tenant/configuration enforces its own
// tenant.tenant_config.read gate, so a caller holding neither permission gets
// the same 403 from both and chat stays denied — exactly the pre-change set.
// Both endpoints return the same `llmProvider` descriptor, so the parse below
// is identical regardless of which one answered.
const LLM_CONFIG_FALLBACK_STATUSES = new Set([403, 404, 501]);

// Multiple consumers (ChatConsole + ChatModelPicker) mount around the
// same time and each call getChatCreds(); coalesce the GET so the
// network only sees one round trip. Promise is held only while in
// flight — a settled fetch clears, so a later refresh hits the
// network again.
let _llmProviderConfigInflight = null;
onAuthIdentityChange(() => { _llmProviderConfigInflight = null; });

async function fetchLlmProviderConfig() {
  if (_llmProviderConfigInflight) return _llmProviderConfigInflight;
  const epoch = getAuthIdentityEpoch();
  const request = (async () => {
    const res = await authedFetch(LLM_PROVIDER_CONFIG_PATH);
    if (res.ok) return res.json();
    if (LLM_CONFIG_FALLBACK_STATUSES.has(res.status)) {
      const legacy = await authedFetch(TENANT_CONFIG_PATH);
      if (legacy.ok) return legacy.json();
      const text = await legacy.text().catch(() => "");
      throw new Error(
        `GET llm provider config (fallback ${TENANT_CONFIG_PATH}) ${legacy.status}: ${text || legacy.statusText}`,
      );
    }
    const text = await res.text().catch(() => "");
    throw new Error(`GET llm provider config ${res.status}: ${text || res.statusText}`);
  })().then((json) => {
    if (getAuthIdentityEpoch() !== epoch) {
      const error = new Error("Authentication identity changed while loading chat credentials");
      error.name = "AuthContextChangedError";
      throw error;
    }
    return json;
  });
  _llmProviderConfigInflight = request;
  try {
    return await request;
  } finally {
    if (_llmProviderConfigInflight === request) _llmProviderConfigInflight = null;
  }
}

// ── Public API ───────────────────────────────────────────────────

export async function getChatCreds() {
  const epoch = getAuthIdentityEpoch();
  const me = await getCurrentUser();
  const tenantId = me?.currentTenant?.tenantId ?? null;
  const role = me?.currentTenant?.role ?? null;

  const json = await fetchLlmProviderConfig();
  if (getAuthIdentityEpoch() !== epoch) {
    const error = new Error("Authentication identity changed while loading chat credentials");
    error.name = "AuthContextChangedError";
    throw error;
  }
  const llm = json?.llmProvider ?? json?.llm_provider ?? null;

  return {
    tenantId,
    role,
    isAdmin: role === "TENANT_ROLE_ADMIN",
    provider: llm ? providerEnumToId(llm.provider) : "",
    model: llm?.model || "",
    taskModel: readTaskModel(tenantId),
    hasApiKey: Boolean(llm && (llm.has_api_key ?? llm.hasApiKey)),
    baseUrl: llm?.base_url ?? llm?.baseUrl ?? "",
    customHostAllowlist: llm?.custom_host_allowlist ?? llm?.customHostAllowlist ?? "",
    customAuthStyle: authStyleEnumToId(llm?.custom_auth_style ?? llm?.customAuthStyle),
    managedDefault: Boolean(llm && (llm.managed_default_active ?? llm.managedDefaultActive)),
  };
}

/**
 * Whether chat is usable for this tenant — the single source of truth for the
 * `hasApiKey || managedDefault` contract documented at the top of this module.
 *
 * True when the tenant has a bring-your-own key on file (`hasApiKey`) OR is
 * being served on Origin's managed key (`managedDefault`). Consumers that gate
 * the chat UI (ChatConsole's first-run takeover, piChat's send guard) MUST use
 * this rather than checking `hasApiKey` alone — otherwise a managed (zero-config)
 * tenant is treated as unconfigured and trapped on the "paste an API key" setup
 * screen with no composer.
 *
 * @param {{ hasApiKey?: boolean, managedDefault?: boolean } | null | undefined} creds
 * @returns {boolean}
 */
export function isChatConfigured(creds) {
  return Boolean(creds && (creds.hasApiKey || creds.managedDefault));
}

export async function setChatCreds({
  provider,
  model,
  taskModel,
  apiKey,
  baseUrl,
  customHostAllowlist,
  customAuthStyle,
}) {
  const epoch = getAuthIdentityEpoch();
  // Task model is local-only; persist before any backend round-trip so
  // a picker that only changes the task model doesn't need to wait.
  if (typeof taskModel === "string") {
    const me = await getCurrentUser();
    const tenantId = me?.currentTenant?.tenantId ?? null;
    if (tenantId) writeTaskModel(tenantId, taskModel || null);
  }

  // O-690's UpdateTenantConfiguration handler runs the `configuration`
  // and `llm_provider` paths independently — either can be present.
  // We only touch the LLM half here so the request stays narrowly
  // scoped to what the picker actually changed.
  //
  // grpc-gateway serializes the response in camelCase, so we send
  // camelCase (`llmProvider`, inner `apiKey`) to match what the
  // proto-json unmarshaler recognizes — snake-case gets dropped.
  const llmPatch = {};
  if (provider) llmPatch.provider = providerIdToEnum(provider);
  if (typeof model === "string") llmPatch.model = model;
  if (typeof apiKey === "string") llmPatch.apiKey = apiKey;
  // Custom (BYO-gateway) fields. proto base_url / custom_host_allowlist map to
  // camelCase in the grpc-gateway JSON, matching the rest of this payload.
  // Empty string clears the field server-side (omit = leave unchanged).
  if (typeof baseUrl === "string") llmPatch.baseUrl = baseUrl;
  if (typeof customHostAllowlist === "string") {
    llmPatch.customHostAllowlist = customHostAllowlist;
  }
  // Custom auth scheme (provider === custom): "bearer" | "api-key" → proto enum.
  // Omit = leave unchanged; the picker only sends it for the custom provider.
  if (typeof customAuthStyle === "string" && customAuthStyle !== "") {
    llmPatch.customAuthStyle = authStyleIdToEnum(customAuthStyle);
  }

  // No LLM-half fields → caller only updated the task model; don't
  // hit the backend.
  if (Object.keys(llmPatch).length === 0) {
    if (typeof taskModel === "string" && getAuthIdentityEpoch() === epoch) notifyChatSettingsChanged();
    return {};
  }

  const res = await authedFetch(TENANT_CONFIG_PATH, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ llmProvider: llmPatch }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(stripProtoFieldPrefix(`Save failed (${res.status}): ${text || res.statusText}`));
  }
  const result = await res.json().catch(() => ({}));
  if (getAuthIdentityEpoch() === epoch) {
    _llmProviderConfigInflight = null;
    notifyChatSettingsChanged();
  }
  return result;
}

function stripProtoFieldPrefix(message) {
  return String(message ?? "")
    .replace(/llm_provider\.[a-z_]+\s*:\s*/gi, "")
    .replace(/^\s+/, "");
}
